import type { FormContextType, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import React, { useState } from 'react';
import { useDebounce } from 'react-use';

import { Calendar } from '@/ui/Calendar';
import { Input } from '@/ui/Input';

import style from './style.module.sass';

const format = (date: number) => (date < 10 ? '0' : '') + date;

const DateWidget = <T, S extends StrictRJSFSchema, F extends FormContextType>(props: WidgetProps<T, S, F>) => {
	const { disabled, formContext, id, name, onBlur, onChange, onFocus, options, placeholder, readonly } = props;
	const value = props.value as unknown;
	const { readonlyAsDisabled = true } = formContext;

	const [isFocused, setIsFocused] = useState(false);
	const [isClosed, setIsClosed] = useState(true);

	// FIXME: use normal
	const [, cancel] = useDebounce(
		() => {
			if (isFocused) {
				setIsFocused(false);
			}
		},
		3000,
		[isClosed, isFocused]
	);

	const handleTextChange = ({ currentTarget }: React.FormEvent<HTMLInputElement>) => {
		onChange(currentTarget.value === '' ? options.emptyValue : currentTarget.value);
	};

	const handleBlur = ({ currentTarget }: React.FormEvent<HTMLInputElement>) => {
		setIsClosed(true);

		onBlur(id, currentTarget.value);
	};

	const handleFocus = ({ currentTarget }: React.FormEvent<HTMLInputElement>) => {
		setIsFocused(true);

		onFocus(id, currentTarget.value);
	};

	const valueAsString = typeof value === 'string' ? value : '';

	return (
		<div className="relative">
			<Input
				disabled={disabled || (readonlyAsDisabled && readonly)}
				id={id}
				name={name}
				onBlur={!readonly ? handleBlur : undefined}
				onChange={!readonly ? handleTextChange : undefined}
				onFocus={!readonly ? handleFocus : undefined}
				placeholder={placeholder}
				className={style.input}
				type="text"
				value={valueAsString}
			/>
			{isFocused && (
				<div className="absolute z-50 pt-1">
					<Calendar
						onSelect={(date) => {
							// FIXME: Need to unify
							const month = format(date.getMonth() + 1);
							const day = format(date.getDate());
							const year = date.getFullYear();

							if (!readonly) {
								onChange(`${year}-${month}-${day}`);

								setIsFocused(false);
							}
						}}
						className="rounded-md border w-fit bg-white"
						mode="single"
					/>
				</div>
			)}
		</div>
	);
};

export { DateWidget };
